import { HttpErrorResponse } from '@angular/common/http';

const SESSION_UNAVAILABLE_MESSAGE =
  'Tu sesión no está disponible. Vuelve a ingresar para continuar.';
const NETWORK_ERROR_MESSAGE = 'Revisa tu conexión e inténtalo nuevamente.';
const VALIDATION_ERROR_MESSAGE = 'Revisa los datos ingresados: alguno no es válido.';
const SERVER_ERROR_MESSAGE =
  'Estamos teniendo problemas para atender la solicitud. Inténtalo en unos segundos.';

const LOGIN_RATE_LIMIT_MESSAGE =
  'Demasiados intentos fallidos. Espera unos minutos e inténtalo nuevamente.';

const BACKEND_MESSAGE_TRANSLATIONS: Readonly<Record<string, string>> = {
  'invalid credentials':
    'La contraseña ingresada no es correcta. Verifícala e inténtalo nuevamente.',
  'account is blocked': 'Tu cuenta se encuentra bloqueada. Comunícate con soporte.',
  'too many failed login attempts. try again later': LOGIN_RATE_LIMIT_MESSAGE,
  'no active session': SESSION_UNAVAILABLE_MESSAGE,
  unauthorized: SESSION_UNAVAILABLE_MESSAGE,
  forbidden: 'No tienes permisos para realizar esta acción.',
};

const SPANISH_WORDS = [
  'artículo',
  'carrito',
  'contraseña',
  'cupón',
  'dirección',
  'distrito',
  'envío',
  'ingresa',
  'ingrese',
  'membresía',
  'pago',
  'producto',
  'sesión',
  'socio',
  'solicitud',
  'suscripción',
  'tarjeta',
  'tiene',
];

/**
 * Letras y signos que en la practica solo aparecen en castellano.
 *
 * La lista blanca sola dejaba caer mensajes del backend perfectamente claros
 * («No tiene una membresía activa») al texto generico de «intente
 * nuevamente», que invita a repetir algo que nunca va a funcionar.
 */
const SPANISH_CHARS = /[áéíóúñ¿¡]/;

/**
 * Convierte errores técnicos/backend a mensajes seguros y claros para cliente final.
 * Nunca debe dejar pasar textos crudos en inglés como "Invalid credentials".
 */
export function clientErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof HttpErrorResponse)) return fallback;

  const backendMessages = extractBackendMessages(error.error);
  const translated = translateBackendMessage(backendMessages);
  if (translated) return translated;

  // Antes que nada: nunca mostrar la validacion cruda del DTO.
  if (backendMessages.some(esValidacionTecnica)) return VALIDATION_ERROR_MESSAGE;

  const spanishMessage = backendMessages.find(isLikelySpanishClientMessage);
  if (spanishMessage) return spanishMessage;

  if (error.status === 0) return NETWORK_ERROR_MESSAGE;
  if (error.status === 429) return LOGIN_RATE_LIMIT_MESSAGE;
  if (error.status === 401 || error.status === 403) return SESSION_UNAVAILABLE_MESSAGE;
  if (error.status >= 500) return SERVER_ERROR_MESSAGE;
  return fallback;
}

export function extractBackendMessages(payload: unknown): readonly string[] {
  if (!payload) return [];
  if (typeof payload === 'string') return [payload.trim()].filter(Boolean);
  if (typeof payload !== 'object') return [];

  const message = (payload as { readonly message?: unknown }).message;
  if (typeof message === 'string') return [message.trim()].filter(Boolean);
  if (Array.isArray(message)) {
    return message
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function translateBackendMessage(messages: readonly string[]): string | null {
  for (const message of messages) {
    const normalized = normalizeMessage(message);
    const translated = BACKEND_MESSAGE_TRANSLATIONS[normalized];
    if (translated) return translated;
  }
  return null;
}

/**
 * Los DTO del backend validan con class-validator, que responde en ingles y
 * con el nombre del campo: «card.cvv must be longer than or equal to 3». Eso
 * llegaba tal cual al socio. Aqui se traduce lo que el formulario no atrapo.
 */
const VALIDACION_TECNICA = /must be|should not|is not a valid|expected|property .* does not exist/i;

function esValidacionTecnica(message: string): boolean {
  return VALIDACION_TECNICA.test(message);
}

function isLikelySpanishClientMessage(message: string): boolean {
  const normalized = normalizeMessage(message);
  if (!normalized) return false;
  if (SPANISH_CHARS.test(normalized)) return true;
  return SPANISH_WORDS.some((word) => normalized.includes(word));
}

function normalizeMessage(message: string): string {
  return message.trim().toLocaleLowerCase('es-PE');
}
